import * as Sentry from '@sentry/react';
import { EnvironmentService } from '../../../../../environment/environment-service';
import { Hasher } from '../../../../api/hasher';
import { HasDependencies, hashString, Role } from '../../../../api/types';
import { FirebaseUser } from '../../../../api/user/types';
import { Domains } from '../../../../constants';
import { ErrorTypes } from '../types';
import { getSentryPanalytTags } from '../utils';
import { ErrorTracker } from './ErrorTracker';

export class DomainScopedErrorTracker implements ErrorTracker, HasDependencies {
  constructor(
    readonly environmentService: EnvironmentService,
    readonly currentUser: FirebaseUser | null,
    readonly currentUserFromLocalStorage: FirebaseUser | null,
    readonly domain: Domains | null,
    readonly domainFromLocalStorage: Domains | null,
    readonly executorRoleFromLocalStorage: Role | null,
    readonly simulatedRole: Role | null,
    readonly simulatedRoleFromLocalStorage: Role | null
  ) {}

  public getDeps = (): (string | undefined)[] => [
    hashString([
      ...this.environmentService.getDeps(),
      Hasher.hash(this.currentUser),
      Hasher.hash(this.currentUserFromLocalStorage),
      this.domain ?? undefined,
      this.domainFromLocalStorage ?? undefined,
      Hasher.hash(this.executorRoleFromLocalStorage),
      Hasher.hash(this.simulatedRole),
      Hasher.hash(this.simulatedRoleFromLocalStorage),
    ]),
  ];

  public trackError = (params: {
    error: Error;
    errorType?: ErrorTypes;
    contexts?: Record<string, any>;
    tags?: Record<string, any>;
    extras?: { key: string; value: Record<string, any> };
  }) => {
    const { error, errorType, contexts, tags, extras } = params;
    const subDomain = this.environmentService.getSubDomain();
    const currentUser = this.currentUser ?? this.currentUserFromLocalStorage;

    if (Sentry) {
      Sentry.withScope((scope) => {
        scope.setUser({
          ...scope.getUser(),
          email: currentUser?.email ?? undefined,
        });
        const panalytTags = getSentryPanalytTags({
          subDomain,
          domain: this.domain,
          domainFromLocalStorage: this.domainFromLocalStorage,
          executorRole: null,
          executorRoleFromLocalStorage: this.executorRoleFromLocalStorage,
          simulatedRole: this.simulatedRole,
          currentUser: this.currentUser,
          currentUserFromLocalStorage: this.currentUserFromLocalStorage,
          errorType,
        });
        scope.setTags({ ...(tags ?? {}), ...panalytTags });
        if (extras) {
          scope.setExtra(extras.key, extras.value);
        }
        Sentry.captureException(error, contexts);
        Sentry.setExtras({ customData: undefined }); // Reset extras so that they are not used within the next request
      });
    }
  };
}
